'use client';

import { RefObject, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Divider, Modal, TextButton } from '@wishket/design-system';

import { CollectionCheckBoxList } from './ScrapBox.parts';

import {
  useMyCollectionsQuery,
  useDeleteArticleFromCollectionMutation,
  useAddYozmITContentsIntoCollectionMutation,
} from '@/entities/collection';
import { getScrollbarStyle } from '@/shared/utils';
import { useDeviceCheck, useUIOverlay } from '@/shared/hooks';
import { useMyCollectionIdsIncludingArticleQuery } from '@/entities/article';

interface ScrapContainerProps {
  isOpen: boolean;
  articleId: number;
  urlIdentifier?: string;
  onClose: () => void;
}

const ScrapContainer = ({
  isOpen,
  articleId,
  urlIdentifier,
  onClose,
}: ScrapContainerProps) => {
  const { isDesktop, isLoading: isDeviceCheckLoading } = useDeviceCheck();
  const { handleOpen } = useUIOverlay();
  const queryClient = useQueryClient();
  const [isFirstLoading, setIsFirstLoading] = useState(true);

  const { data: myCollections, isLoading: isMyCollectionsLoading } =
    useMyCollectionsQuery();
  const {
    data: myCollectionIdsIncludingArticle,
    isLoading: isMyCollectionIdsIncludingArticleLoading,
  } = useMyCollectionIdsIncludingArticleQuery({
    articleId,
  });
  const { mutate: addYozmITContentsIntoCollection, isLoading: isAddLoading } =
    useAddYozmITContentsIntoCollectionMutation();
  const { mutate: deleteArticleFromCollection } =
    useDeleteArticleFromCollectionMutation();

  useEffect(() => {
    if (!isAddLoading) setIsFirstLoading(false);
  }, [isAddLoading]);

  const handleCollectionClick = (collectionId: number) => {
    if (myCollectionIdsIncludingArticle?.includes(collectionId)) {
      deleteArticleFromCollection(
        { collectionId, articleId },
        {
          onSuccess: () => {
            if (urlIdentifier) {
              queryClient.refetchQueries({
                queryKey: ['author', urlIdentifier],
              });
            }
          },
        }
      );
      return;
    }

    addYozmITContentsIntoCollection(
      { collectionId, articleId },
      {
        onSuccess: () => {
          if (urlIdentifier) {
            queryClient.refetchQueries({
              queryKey: ['author', urlIdentifier],
            });
          }
        },
      }
    );
  };

  const openCreateCollectionModal = () => {
    onClose();
    handleOpen('collectionCreate', articleId);
  };

  const checkBoxListData = myCollections?.map((collection) => ({
    label: collection.title,
    value: collection.id,
    isScrapped:
      myCollectionIdsIncludingArticle?.includes(collection.id) ?? false,
  }));

  const checkBoxListProps = {
    data: checkBoxListData ?? [],
    isLoading:
      isFirstLoading ||
      isMyCollectionsLoading ||
      isMyCollectionIdsIncludingArticleLoading,
    onCollectionClick: handleCollectionClick,
  };

  if (isDeviceCheckLoading) return <></>;

  if (isDesktop) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Box className="hidden w-[228px] rounded-xl bg-w-white shadow-graymedium desktop:block">
          <Box
            className={twMerge(
              getScrollbarStyle(),
              'flex max-h-[242px] flex-col gap-4 overflow-y-auto p-6 desktop:pl-6'
            )}
          >
            <CollectionCheckBoxList {...checkBoxListProps} />
          </Box>
          <Divider />
          <Box
            className="flex cursor-pointer items-center gap-1 px-6 py-4"
            onClick={openCreateCollectionModal}
          >
            <TextButton text="컬렉션 만들기" leadingIcon="medium_plus" />
          </Box>
        </Box>
      </motion.div>
    );
  }

  return (
    <Modal
      variant="bottom"
      isOpen={isOpen}
      isFixedHeight={false}
      onClose={onClose}
    >
      <Box
        className={twMerge(
          getScrollbarStyle(),
          'h-[468px] overflow-y-auto px-5 pb-10 pt-5'
        )}
      >
        <Box
          className="flex cursor-pointer items-center gap-1 pb-4"
          onClick={openCreateCollectionModal}
        >
          <TextButton text="컬렉션 만들기" leadingIcon="medium_plus" />
        </Box>
        <Divider />
        <CollectionCheckBoxList className="h-full" {...checkBoxListProps} />
      </Box>
    </Modal>
  );
};

interface ScrapBoxProps extends ScrapContainerProps {
  className?: string;
  scrapBoxRef: RefObject<HTMLDivElement | null>;
}

const ScrapBox = ({
  isOpen,
  articleId,
  className,
  scrapBoxRef,
  urlIdentifier,
  onClose,
}: ScrapBoxProps) => {
  if (!isOpen) return <></>;

  return (
    <Box ref={scrapBoxRef} className={className}>
      <ScrapContainer
        isOpen={isOpen}
        articleId={articleId}
        urlIdentifier={urlIdentifier}
        onClose={onClose}
      />
    </Box>
  );
};

export default ScrapBox;
